import { FlowStep, FlowType } from 'src/frontend/constants/GettingStartedFlows';
import {
    WIRE_SIZING_CONNECTION_ID,
    WIRE_SIZING_TOUR_SNIPPET,
    WIRE_SIZING_TOUR_SNIPPET_NAME,
} from 'src/frontend/constants/tour-snippets';
import { mutate } from 'swr';

import { DiagramSnippetHelpers } from '@diagram/helpers/DiagramSnippetHelpers';
import { DiagramSnippetService } from '@diagram/helpers/DiagramSnippetService';
import { ConnectionService } from '@diagram/services/ConnectionService';
import { SidebarService } from '@diagram/services/SidebarService';
import { gettingStartedTourState } from '@diagram/state/getting-started-tour';
import { SidebarTab } from '@diagram/state/sidebar';

import { DriverService } from 'services/drivers/DriverService';

export enum WireSizingTourStep {
    DRAG_SNIPPET = 'wire-sizing-drag-snippet',
    CLICK_CONNECTION = 'wire-sizing-click-connection',
    SHOW_REQUIREMENTS = 'wire-sizing-show-requirements',
    SHOW_WIRE_SIZE_SETTINGS = 'wire-sizing-show-wire-size-settings',
    SHOW_WIRE_SIZE_SETTINGS_MODAL = 'wire-sizing-show-wire-size-settings-modal',
    SHOW_WIRE_SIZE_SUGGESTIONS = 'wire-sizing-show-wire-size-suggestions',
    APPLY_WIRE_SIZE_SUGGESTION = 'wire-sizing-apply-wire-size-suggestion',
    SHOW_WIRE_SIZE_VALIDATION = 'wire-sizing-show-wire-size-validation',
    VIEW_WIRE_SCHEDULE = 'wire-sizing-view-wire-schedule',
    FINISH = 'wire-sizing-finish',
}

const STEP_SEQUENCE: WireSizingTourStep[] = [
    WireSizingTourStep.DRAG_SNIPPET,
    WireSizingTourStep.CLICK_CONNECTION,
    WireSizingTourStep.SHOW_REQUIREMENTS,
    WireSizingTourStep.SHOW_WIRE_SIZE_SETTINGS,
    WireSizingTourStep.SHOW_WIRE_SIZE_SETTINGS_MODAL,
    WireSizingTourStep.SHOW_WIRE_SIZE_SUGGESTIONS,
    WireSizingTourStep.APPLY_WIRE_SIZE_SUGGESTION,
    WireSizingTourStep.SHOW_WIRE_SIZE_VALIDATION,
    WireSizingTourStep.VIEW_WIRE_SCHEDULE,
    WireSizingTourStep.FINISH,
];

const HIGHLIGHTS: Record<WireSizingTourStep, FlowStep | null> = {
    [WireSizingTourStep.DRAG_SNIPPET]: {
        selector: `[data-tour-snippet=${FlowType.WIRE_SIZING}]`,
        popover: {
            title: 'Drag this example onto your diagram',
            description:
                '<p>This example contains a small DC system you can use to explore wire sizing.</p><p>Drag it onto the canvas to get started.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [WireSizingTourStep.CLICK_CONNECTION]: {
        selector: `[data-connection-hit="${WIRE_SIZING_CONNECTION_ID}"]`,
        popover: {
            title: 'Select a connection',
            description: '<p>Click on this connection to open its specifications.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [WireSizingTourStep.SHOW_REQUIREMENTS]: {
        selector: '[data-tour-wire-requirements]',
        popover: {
            title: 'Set design voltage & current',
            description:
                '<p>Wire sizing needs a <strong>Design Voltage</strong> and <strong>Design Current (Ib)</strong> to derive the correct cable cross-section.</p>' +
                '<p>These values represent the <strong>worst-case operating conditions</strong> - the highest current the cable will ever carry and the lowest voltage the load needs to function.</p>' +
                '<p>Click the <strong>↻ refresh icon</strong> next to each field to run an automatic connection rating and derive these values automatically, or enter them manually.</p>' +
                '<p>👉 <a href="/blog/dc-wire-sizing" target="_blank">Learn more about how wire sizing works</a></p>',
            side: 'left',
            align: 'center',
        },
        showNextButton: true,
    },
    [WireSizingTourStep.SHOW_WIRE_SIZE_SETTINGS]: {
        selector: '[data-tour-wire-size-settings]',
        popover: {
            title: 'Wire sizing settings',
            description:
                '<p>Before calculating wire sizes, make sure your project uses the right <strong>standard</strong> (IEC or NEC)' +
                '<p>Click the <strong>⚙️ Settings</strong> button to open project settings and switch between standards.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [WireSizingTourStep.SHOW_WIRE_SIZE_SETTINGS_MODAL]: {
        selector: '[data-tour-wire-size-settings-modal] .mantine-Modal-content',
        popover: {
            title: 'Project wire sizing settings',
            description:
                '<p>Choose the <strong>standard</strong> (IEC or NEC) that applies to your project.</p>' +
                '<p>Also select your preferred <strong>measurement system</strong> (metric or imperial).</p>' +
                '<p>Close the modal when you are ready to proceed.</p>',
            side: 'right',
            align: 'center',
        },
    },
    [WireSizingTourStep.SHOW_WIRE_SIZE_SUGGESTIONS]: {
        selector: '[data-tour-wire-size-suggestions]',
        popover: {
            title: 'Wire cross-section suggestions',
            description:
                '<p>Click on <strong>Suggested Wire Sizes</strong> to expand and see the recommended wire cross-section options for this cable.</p>' +
                '<p>The suggestions are derived from your design voltage, current, and installation conditions, then validated against IEC and NEC standards.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [WireSizingTourStep.APPLY_WIRE_SIZE_SUGGESTION]: {
        selector: '[data-tour-wire-size-suggestions]',
        popover: {
            title: 'Apply a wire size suggestion',
            description:
                '<p>Choose a recommended wire size from the dropdown and click <strong>Apply suggestion</strong> to update the connection.</p>' +
                '<p>This will set the wire cross-section and other properties based on the selected standard.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [WireSizingTourStep.SHOW_WIRE_SIZE_VALIDATION]: {
        selector: '[data-tour-wire-size-validation]',
        popover: {
            title: 'Wire sizing validation',
            description:
                '<p>The three colored segments show the validation status for:</p>' +
                '<ul>' +
                '<li><strong>Current carrying capacity</strong> - whether the wire can safely carry the design current without overheating</li>' +
                '<li><strong>Voltage drop</strong> - whether the voltage at the load stays within acceptable limits</li>' +
                '<li><strong>Overload protection</strong> - whether the protection device properly safeguards the cable</li>' +
                '</ul>' +
                '<p>Hover over each segment to see detailed results.</p>',
            side: 'left',
            align: 'end',
        },
        delay: 700,
        showNextButton: true,
    },
    [WireSizingTourStep.VIEW_WIRE_SCHEDULE]: {
        selector: '[data-sidebar-type="wireSchedule"]',
        popover: {
            title: 'Wire Schedule',
            description:
                '<p>Click on the <strong>Wire Schedule</strong> tab to view a comprehensive overview of all connections in the diagram.</p>' +
                '<p>The wire schedule shows all the connections, their specifications, and validation results.</p>',
            side: 'left',
            align: 'end',
        },
    },
    [WireSizingTourStep.FINISH]: {
        selector: '[data-diagram-sidebar]',
        popover: {
            title: "You're all set!",
            description:
                "<p>You've completed the wire sizing tour. Try configuring and running the wire sizing analysis on other connections.</p>" +
                '<h3>More resources</h3>' +
                '<p>👉 <a href="https://www.youtube.com/watch?v=ZVgxERVex2A" target="_blank">Dive deeper into wire sizing with our webinar in collaboration with Fraunhofer IPA</a></p>',
            side: 'left',
            align: 'center',
        },
        delay: 200,
        isLastStep: true,
    },
};

const tourConnectionId = () => gettingStartedTourState.tourTargetConnectionId ?? WIRE_SIZING_CONNECTION_ID;

const WireSizingDriverService = {
    start: async (initStep: WireSizingTourStep = WireSizingTourStep.DRAG_SNIPPET) => {
        DriverService.request(FlowType.WIRE_SIZING);

        try {
            if (initStep === WireSizingTourStep.DRAG_SNIPPET) {
                await WireSizingDriverService.addSnippet();
            }
            DriverService.setStep(FlowType.WIRE_SIZING, initStep);
        } catch (error) {
            console.error('Failed to start wire sizing tour', error);
            DriverService.cancel(FlowType.WIRE_SIZING);
        }
    },

    showHighlight: (step: WireSizingTourStep) => {
        const highlight = HIGHLIGHTS[step];

        if (!highlight) {
            return null;
        }

        const config =
            step === WireSizingTourStep.CLICK_CONNECTION
                ? { ...highlight, selector: `[data-connection-hit="${tourConnectionId()}"]` }
                : highlight;

        return DriverService.showHighlight(FlowType.WIRE_SIZING, config, {
            onAdvance: () => WireSizingDriverService.advance(),
        });
    },

    hideOnSnippetDrag: () => {
        return DriverService.hideOnDrag(() => WireSizingDriverService.showHighlight(WireSizingTourStep.DRAG_SNIPPET));
    },

    advance: () => {
        DriverService.advance(FlowType.WIRE_SIZING, STEP_SEQUENCE);
    },

    onSnippetDropped: (name: string, connectionIds: Record<string, string>) => {
        const isTourSnippet = name === WIRE_SIZING_TOUR_SNIPPET_NAME;

        if (gettingStartedTourState.activeTourStep === WireSizingTourStep.DRAG_SNIPPET && isTourSnippet) {
            gettingStartedTourState.tourTargetConnectionId = connectionIds[WIRE_SIZING_CONNECTION_ID] ?? null;

            WireSizingDriverService.advance();
        }
    },

    onConnectionSelected: (connectionId: string) => {
        if (
            gettingStartedTourState.activeTourStep === WireSizingTourStep.CLICK_CONNECTION &&
            WireSizingDriverService.isTourConnection(connectionId)
        ) {
            SidebarService.setActiveTab(SidebarTab.SPECIFICATIONS);
            WireSizingDriverService.advance();
        }
    },

    onSettingsClick: () => {
        if (gettingStartedTourState.activeTourStep === WireSizingTourStep.SHOW_WIRE_SIZE_SETTINGS) {
            WireSizingDriverService.advance();
        }
    },

    onSettingsModalClose: () => {
        if (gettingStartedTourState.activeTourStep === WireSizingTourStep.SHOW_WIRE_SIZE_SETTINGS_MODAL) {
            WireSizingDriverService.advance();
        }
    },

    onSuggestionExpanded: () => {
        if (gettingStartedTourState.activeTourStep === WireSizingTourStep.SHOW_WIRE_SIZE_SUGGESTIONS) {
            WireSizingDriverService.advance();
        }
    },

    onSuggestionApplied: () => {
        if (gettingStartedTourState.activeTourStep === WireSizingTourStep.APPLY_WIRE_SIZE_SUGGESTION) {
            WireSizingDriverService.advance();
        }
    },

    onWireScheduleOpened: () => {
        if (gettingStartedTourState.activeTourStep === WireSizingTourStep.VIEW_WIRE_SCHEDULE) {
            WireSizingDriverService.advance();
        }
    },

    isTourConnection: (connectionId: string) => {
        const connection = ConnectionService.getById(connectionId);

        if (!connection) {
            return false;
        }

        return connection.id === tourConnectionId();
    },

    addSnippet: async () => {
        try {
            const { docs } = await DiagramSnippetService.list();

            const exists = docs.some((snippet) => snippet.name === WIRE_SIZING_TOUR_SNIPPET_NAME);

            if (exists) {
                return;
            }

            await DiagramSnippetService.create({
                name: WIRE_SIZING_TOUR_SNIPPET_NAME,
                ...WIRE_SIZING_TOUR_SNIPPET,
            });

            await mutate(DiagramSnippetHelpers.swr.list, undefined, { revalidate: true });
        } catch {
            DriverService.cancel(FlowType.WIRE_SIZING);
        }
    },
};

export { WireSizingDriverService };
